import React, { useEffect, useState } from 'react'
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form'
import { TextField } from '../../components/Forms/TextField'
import { Button } from '../../components/Button/Button'
import { CampaignStatus } from 'domain/entities/Campaign'
import { Inventory } from 'domain/entities/Inventory'
import { InventoryList } from '../inventory/InventoryList'
import { ErrorDialog } from 'presentation/components/Dialogs/ErrorDialog/ErrorDialog'

export type FormValues = {
  id?: string
  title: string
  vastUrl: string
  status: CampaignStatus
}

type CampaignFormProps = {
  onSubmit: (data: FormValues) => Promise<string>
  onCancel: () => void
  onInventoryUnlink: (campaignID: string, inventoryID: string) => void
  onInventoryLink: (campaignID: string, inventoryID: string) => void
  initialData?: FormValues | null
  connectedInventoryList?: Inventory[]
  availableInventoryList?: Inventory[]
  loading: boolean
}

export const CampaignForm: React.FC<CampaignFormProps> = ({
  onSubmit,
  onCancel,
  onInventoryUnlink,
  onInventoryLink,
  initialData,
  connectedInventoryList = [],
  availableInventoryList = [],
  loading = false,
}) => {
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      title: initialData?.title || '',
      vastUrl: initialData?.vastUrl || '',
      status: initialData?.status ?? CampaignStatus.Live,
    },
  })
  const [connected, setConnected] = useState<Inventory[]>(
    connectedInventoryList
  )
  const [available, setAvailable] = useState<Inventory[]>(
    availableInventoryList
  )

  const [error, setError] = useState<string | null>(null)

  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = methods

  const handleFormSubmit: SubmitHandler<FormValues> = async (data) => {
    let id: string
    setError(null)

    try {
      id = await onSubmit({
        ...data,
      })

      const campaignId = initialData?.id || id

      const added = connected.filter(
        (i) => !connectedInventoryList.find((j) => j.id === i.id)
      )
      const removed = available.filter(
        (i) => !availableInventoryList.find((j) => j.id === i.id)
      )

      await Promise.all([
        ...added.map((i) => onInventoryLink(campaignId, i.id)),
        ...removed.map((i) => onInventoryUnlink(campaignId, i.id)),
      ])

      onCancel()
    } catch (e) {
      setError('Failed to submit campaign form. \n')
    }
  }

  const unlink = (inv: Inventory) => {
    setConnected((cs) => cs.filter((i) => i.id !== inv.id))
    setAvailable((as) => [...as, inv])
  }

  const link = (inv: Inventory) => {
    setAvailable((as) => as.filter((i) => i.id !== inv.id))
    setConnected((cs) => [...cs, inv])
  }

  const connectedInventoriesAccessors = (inv: Inventory) => (
    <>
      <Button
        type="button"
        variant="delete"
        onClick={() => {
          unlink(inv)
        }}
      >
        Unlink
      </Button>
    </>
  )

  const actionsAccessor = (inv: Inventory) => (
    <>
      <Button
        type="button"
        variant="edit"
        onClick={() => {
          link(inv)
        }}
      >
        Link
      </Button>
    </>
  )

  useEffect(() => {
    reset({
      title: initialData?.title || '',
      vastUrl: initialData?.vastUrl || '',
      status: initialData?.status ?? CampaignStatus.Live,
    })
    setConnected(connectedInventoryList)
    setAvailable(availableInventoryList)
  }, [initialData, connectedInventoryList, availableInventoryList, reset])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {error && (
          <ErrorDialog
            isOpen={!!error}
            onClose={() => setError(null)}
            supportUrl="mailto:support@adshines.io"
            title="Problem creating campaign"
            description={error}
          />
        )}
        <TextField
          name="title"
          label="Campaign name"
          validation={{ required: 'The campaign name is required' }}
          required={true}
        />
        <TextField
          name="vastUrl"
          label="Ad Source URL"
          validation={{ required: 'The ad source URL is required' }}
          required={true}
        />
        <div className="form-group">
          <label htmlFor="connectedInventories">Connected inventories</label>
          <InventoryList
            onEdit={() => {}}
            data={connected}
            loading={loading}
            refetch={() => {}}
            displayedColumns={['Title', 'Actions']}
            actionsAccessor={connectedInventoriesAccessors}
          />
        </div>
        <div className="form-group">
          <label htmlFor="availableInventory">Available inventories</label>
          <InventoryList
            onEdit={() => {}}
            data={available}
            loading={loading}
            refetch={() => {}}
            displayedColumns={['Title', 'Actions']}
            actionsAccessor={actionsAccessor}
          />
        </div>
        <div className="form-actions">
          <Button variant="cancel" onClick={onCancel} type="button">
            Cancel
          </Button>
          <Button variant="primary" disabled={!isValid} type="submit">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
